import { DashboardRouteNames, ProductRouteNames, ordersRouteNames, paymentSettingsRouteNames, subscriptionsRouteNames, transactionsRouteNames } from '@/routes/constants/location';

import { Route } from 'vue-router'

export const userRoutesPermissionChecker = () => {
    function checkFormsPermissions(locationLevelPermissions: any, userLevelPermissions: any)  {
        return locationLevelPermissions['forms_enabled'] && userLevelPermissions.includes("forms.write")
    }
    function checkQuizzesPermissions(locationLevelPermissions: any, userLevelPermissions: any)  {
        return locationLevelPermissions['quizzes_enabled'] && userLevelPermissions.includes("quizzes.write")
    }
    function checkSurveysPermissions(locationLevelPermissions: any, userLevelPermissions: any)  {
        return locationLevelPermissions['surveys_enabled'] && userLevelPermissions.includes("surveys.write")
    }
    function checkQrCodesPermissions(locationLevelPermissions: any, userLevelPermissions: any)  {
        return locationLevelPermissions['qr_codes_enabled'] && userLevelPermissions.includes("qrcodes.write")
    }
    function checkLaunchpadPermissions(locationLevelPermissions: any, userLevelPermissions: any, hideLaunchpad: boolean)  {
        return locationLevelPermissions['launchpad_enabled'] && userLevelPermissions.includes("launchpad.write") && !hideLaunchpad
    }
    return {
        checkFormsPermissions,
        checkQuizzesPermissions,
        checkSurveysPermissions,
        checkQrCodesPermissions,
        checkLaunchpadPermissions
    }
}

export const checkPaymentPermissions = (routeName: string, userPermissions: string[]): boolean => {
    const permissionMap: Record<string, string> = {
        [ordersRouteNames.orders]: 'payments/orders.readonly',
        [subscriptionsRouteNames.subscriptions]: 'payments/subscriptions.readonly',
        [transactionsRouteNames.transactions]: 'payments/transactions.readonly',
        [ProductRouteNames.products]: 'products.readonly'
    }

    // Handle payment settings routes
    const paymentSettingsRoutes = [
        paymentSettingsRouteNames.settings,
        paymentSettingsRouteNames.settingsReceipts,
        paymentSettingsRouteNames.settingsTaxes,
        paymentSettingsRouteNames.settingsNotifications,
        paymentSettingsRouteNames.settingsShipping,
        paymentSettingsRouteNames.settingsSubscriptions,
        paymentSettingsRouteNames.settingsPaymentLinks,
        paymentSettingsRouteNames.settingsMiscellaneousCharges
    ]

    if (paymentSettingsRoutes.includes(routeName)) {
        return userPermissions.includes('payments/settings.readonly')
    }

    const requiredPermission = permissionMap[routeName]
    if (!requiredPermission) {
        console.warn(`No permission mapping found for route: ${routeName}`)
        return false
    }

    return userPermissions.includes(requiredPermission)
}

/**
 * Composable for handling permissions checks and redirects
 */
export const usePermissionsCheck = () => {
    /**
     * Checks if user has required permission(s) and redirects to dashboard if not
     * @param userScopes - Array of user permission scopes
     * @param requiredPermissions - The permission scope(s) required for access (string or array)
     * @param router - Vue router instance
     * @param route - Current route object
     * @param requireAll - If true, user must have ALL permissions; if false, user needs ANY permission
     * @returns boolean - true if user has access, false if redirected
     */
    const checkAndRedirectIfNoAccess = (
        userScopes: string[], 
        requiredPermissions: string | string[], 
        router: any, 
        route: Route,
        requireAll: boolean = true
    ): boolean => {
        const permissions = Array.isArray(requiredPermissions) ? requiredPermissions : [requiredPermissions]
        
        let hasAccess: boolean
        if (requireAll) {
            // User must have ALL permissions
            hasAccess = permissions.every(permission => userScopes.includes(permission))
        } else {
            // User must have at least ONE permission
            hasAccess = permissions.some(permission => userScopes.includes(permission))
        }

        if (!hasAccess) {
            router.push({
                name: DashboardRouteNames.default,
                params: route.params,
                query: route.query,
                replace: true
            })
            return false
        }
        return true
    }

    return { checkAndRedirectIfNoAccess }
}
